import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { Option } from "@recore-solutions/nstar-lib";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import { resizeObservable } from "../../resize-observable";

@Component({
	selector: "app-dropdown-select",
	templateUrl: "./dropdown-select.component.html",
	styleUrls: ["./dropdown-select.component.scss"],
})
export class DropdownSelectComponent implements OnInit, AfterViewInit {
	@Input() label: string;
	@Input() tooltip: string;
	@Input() pluralLabel: string;
	@Input() options: Option[];
	@Input() control: UntypedFormControl;
	@Input() multiple = false;
	@Input() dropdownContainerClass = "";
	@Input() dropdownMenuClass = "";
	@Input() dropdownToggleClass = "";
	@Input() dropdownItemClass = "";
	@Input() dropdownItemAllClass = "";

	@ViewChild("dropdownMenu") dropdownMenu: ElementRef<any>;

	label$: Observable<string>;
	top$: Observable<number>;

	constructor() {}

	ngOnInit(): void {
		if (!this.control) {
			this.control = new UntypedFormControl(this.multiple ? [] : null);
		}
		this.pluralLabel = this.pluralLabel || this.label + "s";
		this.label$ = this.control.valueChanges.pipe(
			startWith(this.control.value),
			map(this.mapOptionsToOptionFilterLabel(this.options, this.label, this.pluralLabel))
		);
	}

	ngAfterViewInit(): void {
		this.top$ = resizeObservable(this.dropdownMenu.nativeElement).pipe(
			map(this.mapResizeObserverEntriesToTop)
		);
	}

	mapResizeObserverEntriesToTop = (entries: ResizeObserverEntry[]) =>
		entries[0]?.target?.getBoundingClientRect?.()?.top || 0;

	mapOptionsToOptionFilterLabel(
		allOptions: Option[],
		name: string,
		plural: string
	): (selected: string[]) => string {
		return (selected: string[]): string => {
			// If something is selected and either we only allow one selection or only have one selection
			if (!!selected && (!this.multiple || selected.length === 1)) {
				// Return the name of the one selected, either the only member of the array (if multiple allowed) or the value itself
				return (
					allOptions.find((c) => c.key === (this.multiple ? selected[0] : selected)) || {
						value: name,
					}
				).value;
			} else {
				if (!this.multiple) {
					return name;
				} else {
					const num = selected.length === allOptions.length ? "all" : selected.length;
					return plural + " (" + num + ")";
				}
			}
		};
	}

	toggleOption(control: UntypedFormControl, options: Option[], option: Option): void {
		if (!this.multiple) {
			this.control.setValue(option.key);
		} else {
			const newValue = control.value || [];
			const index = !!option ? newValue.indexOf(option.key) : -1;
			if (index > -1) {
				control.setValue(newValue.filter((o) => o !== option.key));
			} else if (!!option) {
				control.setValue(newValue.concat(option.key));
			} else {
				control.setValue(newValue.length < options.length ? options.map((o) => o.key) : []);
			}
		}
	}

	isCurrentOption(option: Option): boolean {
		return !this.multiple
			? this.control.value === option.key
			: this.control.value.indexOf(option.key) > -1;
	}
}
